(() => {
  if (customElements.get('vnd-stories-carousel')) return;
  class StoriesCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.slides = [...this.querySelectorAll('.vnd-stories-hero__slide')];
      if (this.slides.length < 2) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.index = 0;
      this.request = 0;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.playing = !this.motion.matches;
      this.hovered = false;
      this.inView = false;
      this.dots = [...this.querySelectorAll('[data-slide]')];
      this.previous = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      this.counter = this.querySelector('[data-carousel-counter]');
      this.addEventListener('click', event => {
        const button = event.target.closest('button[data-slide]');
        if (!button || !this.contains(button)) return;
        this.navigate(Number(button.dataset.slide));
      }, options);
      this.previous?.addEventListener('click', () => this.navigate(this.index - 1), options);
      this.next?.addEventListener('click', () => this.navigate(this.index + 1), options);
      // Keep a manually selected scene in place for this visit.
      this.addEventListener('focusin', () => { this.playing = false; this.syncTimer(); }, options);
      this.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.navigate(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') { this.hovered = true; this.syncTimer(); }
      }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.syncTimer(); }, options);
      const stage = this.querySelector('.vnd-stories-hero__stage');
      stage.addEventListener('pointerdown', event => {
        if (event.isPrimary) this.swipe = { x: event.clientX, y: event.clientY, id: event.pointerId };
      }, options);
      stage.addEventListener('pointerup', event => {
        if (!this.swipe || this.swipe.id !== event.pointerId) return;
        const dx = event.clientX - this.swipe.x, dy = event.clientY - this.swipe.y;
        this.swipe = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.navigate(this.index + (dx < 0 ? 1 : -1));
        }
      }, options);
      stage.addEventListener('pointercancel', () => { this.swipe = null; }, options);
      stage.addEventListener('dragstart', event => event.preventDefault(), options);
      document.addEventListener('visibilitychange', () => this.syncTimer(), options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.playing = false;
        this.syncTimer();
      }, options);
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.syncTimer();
      });
      this.observer.observe(this);
      this.slides.forEach((slide, i) => { slide.hidden = i !== 0; });
      this.render();
      this.querySelector('.vnd-stories-hero__controls').hidden = false;
      if (this.previous) this.previous.hidden = false;
      if (this.next) this.next.hidden = false;
      this.classList.add('is-ready');
      this.syncTimer();
    }
    navigate(index) {
      this.playing = false;
      this.syncTimer();
      this.show(index, true);
    }
    async show(index, announce = false) {
      const next = (index + this.slides.length) % this.slides.length;
      const request = ++this.request;
      const signal = this.controller.signal;
      const image = this.slides[next].querySelector('img');
      image.loading = 'eager';
      try { await image.decode(); } catch {
        if (!signal.aborted && request === this.request) this.syncTimer();
        return;
      }
      if (signal.aborted || !this.isConnected || request !== this.request) return;
      this.slides[this.index].hidden = true;
      this.index = next;
      this.slides[next].hidden = false;
      this.render();
      if (announce) this.querySelector('[data-status]').textContent =
        this.slides[next].getAttribute('aria-label');
      this.syncTimer();
    }
    render() {
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = `${this.index + 1} / ${this.slides.length}`;
      this.warmNext();
    }
    async warmNext() {
      const index = this.index;
      const generation = this.warmGeneration = (this.warmGeneration || 0) + 1;
      const signal = this.controller.signal;
      const image = this.slides[index].querySelector('img');
      // Wait for this scene to load and decode before yielding a paint/idle opportunity.
      try {
        if (!image.complete) await new Promise((resolve, reject) => {
          image.addEventListener('load', resolve, { once: true, signal });
          image.addEventListener('error', reject, { once: true, signal });
        });
        if (!image.naturalWidth) return;
        await image.decode();
      } catch { return; }
      if (signal.aborted || !this.isConnected || index !== this.index || generation !== this.warmGeneration) return;
      requestAnimationFrame(() => {
        const warm = () => {
          if (signal.aborted || !this.isConnected || index !== this.index || generation !== this.warmGeneration) return;
          const connection = navigator.connection;
          if (connection?.saveData || connection?.effectiveType === 'slow-2g') return;
          this.slides[(index + 1) % this.slides.length].querySelector('img').loading = 'eager';
        };
        if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 2000 });
        else setTimeout(warm, 0);
      });
    }
    syncTimer() {
      clearTimeout(this.timer);
      if (this.playing && !this.hovered && this.inView && !document.hidden) {
        this.timer = setTimeout(() => this.show(this.index + 1), 6000);
      }
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.request++;
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
    }
  }
  customElements.define('vnd-stories-carousel', StoriesCarousel);
})();
